import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hrApi } from "../../services/hrApi";

function Modal({ open, title, children, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl border border-gray-200 dark:border-neutral-800 text-gray-900 dark:text-white">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-gray-300"
          >
            Close
          </button>
        </div>

        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const emptyForm = {
  title: "",
  code: "",
  position_mailbox: "",
  planned_headcount: 1,
  is_leadership: 0,
  reports_to_position_id: "",
  department_id: "",
  team_id: "",
};

export default function HRPositionsPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [parentOpen, setParentOpen] = useState(false);

  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [parentForm, setParentForm] = useState({
    id: null,
    title: "",
    reports_to_position_id: "",
  });

  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);

  const loadPositions = async () => {
  try {
    setLoading(true);
    setError("");

    const [posRes, depRes, teamRes] = await Promise.all([
      hrApi.getPositions(),
      hrApi.getDepartments(),
      hrApi.getTeams(),
    ]);

    setRows(posRes?.data?.data || []);
    setDepartments(depRes?.data?.data || []);
    setTeams(teamRes?.data?.data || []);
  } catch (err) {
    console.error("Failed to load positions:", err);
    setError(err?.response?.data?.message || err.message || "Failed to load positions.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    loadPositions();
  }, []);

  const filteredRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((r) => {
      const haystack = [
        r.title,
        r.code,
        r.department_name,
        r.team_name,
        r.reports_to_title,
        r.reports_to_position_title,
        r.position_mailbox,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [rows, q]);

  const allParentOptions = useMemo(() => {
    return rows
      .map((r) => ({
        id: r.id,
        label: `${r.title || "-"} (${r.code || "-"})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const openCreate = () => {
  setCreateForm({
    ...emptyForm,
    planned_headcount: 1,
    is_leadership: 0,
    department_id: "",
    team_id: "",
  });
  setCreateOpen(true);
};

  const openEdit = (row) => {
  setEditForm({
    id: row.id,
    title: row.title || "",
    code: row.code || "",
    position_mailbox: row.position_mailbox || "",
    planned_headcount: Number(row.planned_headcount || 1),
    is_leadership: Number(row.is_leadership || 0),
    reports_to_position_id: row.reports_to_position_id || "",
    department_id: row.department_id || "",
    team_id: row.team_id || "",
  });
  setEditOpen(true);
};

  const openParent = (row) => {
    setParentForm({
      id: row.id,
      title: row.title || "",
      reports_to_position_id: row.reports_to_position_id || "",
    });
    setParentOpen(true);
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      setError("");

      const payload = {
  title: createForm.title?.trim(),
  code: createForm.code?.trim(),
  position_mailbox: createForm.position_mailbox?.trim() || null,
  planned_headcount: Number(createForm.planned_headcount || 1),
  is_leadership: createForm.is_leadership ? 1 : 0,
  reports_to_position_id: createForm.reports_to_position_id || null,
  department_id: createForm.department_id || null,
  team_id: createForm.team_id || null,
};

      if (!payload.title || !payload.code) {
        setError("Title and code are required.");
        return;
      }

      await hrApi.createPosition(payload);
      setCreateOpen(false);
      await loadPositions();
    } catch (err) {
      console.error("Create position error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to create position.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      setError("");

      const payload = {
  title: editForm.title?.trim(),
  code: editForm.code?.trim(),
  position_mailbox: editForm.position_mailbox?.trim() || null,
  planned_headcount: Number(editForm.planned_headcount || 1),
  is_leadership: editForm.is_leadership ? 1 : 0,
  reports_to_position_id: editForm.reports_to_position_id || null,
  department_id: editForm.department_id || null,
  team_id: editForm.team_id || null,
};

      if (!payload.title || !payload.code) {
        setError("Title and code are required.");
        return;
      }

      await hrApi.updatePosition(editForm.id, payload);
      setEditOpen(false);
      await loadPositions();
    } catch (err) {
      console.error("Update position error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to update position.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveParent = async () => {
    try {
      setSaving(true);
      setError("");

      await hrApi.updatePosition(parentForm.id, {
        reports_to_position_id: parentForm.reports_to_position_id || null,
      });

      setParentOpen(false);
      await loadPositions();
    } catch (err) {
      console.error("Change parent error:", err);
      setError(err?.response?.data?.message || err.message || "Failed to update parent.");
    } finally {
      setSaving(false);
    }
  };

  const renderFormFields = (form, setForm, excludeSelfId = null) => {
    const parentOptions = allParentOptions.filter((p) => p.id !== excludeSelfId);

    return (
      <div className="space-y-4">

  <div>
    <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Title</div>
    <input
      value={form.title}
      onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
      className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
                 dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
      placeholder="Position title"
    />
  </div>

  <div>
    <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Code</div>
    <input
      value={form.code}
      onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
      className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
                 dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
      placeholder="POS_EXAMPLE"
    />
  </div>

  <div>
  <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Department</div>
  <select
    value={form.department_id || ""}
    onChange={(e) => setForm((s) => ({ ...s, department_id: e.target.value }))}
    className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
               dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    <option value="">No department</option>
    {departments.map((d) => (
      <option key={d.id} value={d.id}>
        {d.name}
      </option>
    ))}
  </select>
</div>

<div>
  <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Team</div>
  <select
    value={form.team_id || ""}
    onChange={(e) => setForm((s) => ({ ...s, team_id: e.target.value }))}
    className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
               dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    <option value="">No team</option>
    {teams.map((t) => (
      <option key={t.id} value={t.id}>
        {t.name}
      </option>
    ))}
  </select>
</div>

  <div>
    <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Mailbox</div>
    <input
      value={form.position_mailbox}
      onChange={(e) => setForm((s) => ({ ...s, position_mailbox: e.target.value }))}
      className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
                 dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
      placeholder="team@modularobjects.com"
    />
  </div>

  <div>
    <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Planned headcount</div>
    <input
      type="number"
      min="1"
      value={form.planned_headcount}
      onChange={(e) =>
        setForm((s) => ({ ...s, planned_headcount: e.target.value }))
      }
      className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
                 dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
    />
  </div>

  <div>
    <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">Reports to</div>
    <select
      value={form.reports_to_position_id || ""}
      onChange={(e) =>
        setForm((s) => ({ ...s, reports_to_position_id: e.target.value }))
      }
      className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
                 dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
    >
      <option value="">No parent</option>
      {parentOptions.map((p) => (
        <option key={p.id} value={p.id}>
          {p.label}
        </option>
      ))}
    </select>
  </div>

  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
    <input
      type="checkbox"
      checked={Number(form.is_leadership) === 1}
      onChange={(e) =>
        setForm((s) => ({ ...s, is_leadership: e.target.checked ? 1 : 0 }))
      }
      className="accent-black dark:accent-white"
    />
    Leadership position
  </label>

</div>
    );
  };

  return (
    <div className="p-6 space-y-5 text-gray-900 dark:text-white">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Positions</h1>
<p className="text-sm text-gray-500 dark:text-gray-400">
            Upravljanje pozicijama, parent-child vezama i organizacionom strukturom.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">

  <button
    onClick={() => navigate("/hr/org-chart")}
    className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-black
               dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    Open Org Chart
  </button>

  <button
    onClick={loadPositions}
    className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-black
               dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    Refresh
  </button>

  <button
    onClick={openCreate}
    className="px-4 py-2 rounded-xl bg-black text-white
               dark:bg-white dark:text-black"
  >
    Add position
  </button>

</div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, code, department, mailbox..."
            className="w-full md:max-w-md px-3 py-2 rounded-xl border border-gray-300 bg-white text-black dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
          />

          <div className="text-sm text-gray-500 dark:text-gray-400">
            Total: {filteredRows.length}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading positions...</div>
      ) : (
        <div className="space-y-3">
          {filteredRows.map((row) => (
            <div
              key={row.id}
              className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-4"
            >
              <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {row.title || "-"}
                    </h3>

                    <span className="text-xs px-2 py-0.5 rounded-full border border-gray-300 text-gray-600 dark:border-neutral-700 dark:text-gray-300">
                      {row.code || "-"}
                    </span>

                    {Number(row.is_leadership) === 1 ? (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                        leadership
                      </span>
                    ) : null}
                  </div>

                  <div className="text-sm text-gray-500 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    <span>Department: {row.department_name || "-"}</span>
                    <span>Team: {row.team_name || "-"}</span>
                    <span>Planned: {row.planned_headcount || 1}</span>
                    <span>Mailbox: {row.position_mailbox || "-"}</span>
                    <span>
                      Reports to:{" "}
                      {row.reports_to_position_title || row.reports_to_title || "-"}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
  <button
    onClick={() => openEdit(row)}
    className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
               dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    Edit
  </button>

  <button
    onClick={() => openParent(row)}
    className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
               dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    Change parent
  </button>

  <button
    onClick={() => navigate(`/hr/org-chart?positionId=${row.id}`)}
    className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
               dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    Open chart
  </button>
</div>
              </div>
            </div>
          ))}

          {!filteredRows.length ? (
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-6 text-sm text-gray-500 dark:text-gray-400">
              No positions found.
            </div>
          ) : null}
        </div>
      )}

      <Modal
        open={createOpen}
        title="Add position"
        onClose={() => !saving && setCreateOpen(false)}
      >
        {renderFormFields(createForm, setCreateForm, null)}

        <div className="flex gap-2 mt-5">
          <button
            onClick={handleCreate}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-black text-white disabled:opacity-60"
          >
            {saving ? "Saving..." : "Create"}
          </button>
          <button
            onClick={() => setCreateOpen(false)}
            disabled={saving}
            className="px-4 py-2 rounded-xl border border-gray-300"
          >
            Cancel
          </button>
        </div>
      </Modal>

      <Modal
        open={editOpen}
        title={`Edit position${editForm.title ? `: ${editForm.title}` : ""}`}
        onClose={() => !saving && setEditOpen(false)}
      >
        {renderFormFields(editForm, setEditForm, editForm.id)}

        <div className="flex gap-2 mt-5">
          <button
            onClick={handleSaveEdit}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-black text-white disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
          <button
            onClick={() => setEditOpen(false)}
            disabled={saving}
            className="px-4 py-2 rounded-xl border border-gray-300"
          >
            Cancel
          </button>
        </div>
      </Modal>

      <Modal
        open={parentOpen}
        title={`Change parent${parentForm.title ? `: ${parentForm.title}` : ""}`}
        onClose={() => !saving && setParentOpen(false)}
      >
        <div className="space-y-4">
  <div>
    <div className="text-sm mb-1 text-gray-600 dark:text-gray-400">
      Reports to
    </div>
    <select
      value={parentForm.reports_to_position_id || ""}
      onChange={(e) =>
        setParentForm((s) => ({
          ...s,
          reports_to_position_id: e.target.value,
        }))
      }
      className="w-full px-3 py-2 rounded-xl border border-gray-300 bg-white text-black
                 dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
    >
      <option value="">No parent</option>
      {allParentOptions
        .filter((p) => p.id !== parentForm.id)
        .map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
    </select>
  </div>

  <div className="flex gap-2">
    <button
      onClick={handleSaveParent}
      disabled={saving}
      className="px-4 py-2 rounded-xl bg-black text-white dark:bg-white dark:text-black disabled:opacity-60"
    >
      {saving ? "Saving..." : "Save parent"}
    </button>
    <button
      onClick={() => setParentOpen(false)}
      disabled={saving}
      className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-black
                 dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
    >
      Cancel
    </button>
  </div>
</div>
      </Modal>
    </div>
  );
}